/**
 * CR5d — Workbench capability deny hint (reason + suggestedAction CTA)
 */

import React from 'react';
import { Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { css } from '@shared/utils/omitUndefined';
import { getSuggestedAction } from '@shared/capability/suggested-action-registry';
import StepUpMfaModal from '../auth/StepUpMfaModal';
import styles from './CapabilityDeniedHint.module.css';

const SUGGESTED_ACTION_FALLBACK_LABELS: Record<string, string> = {
  request_access: '권한 요청',
  step_up_mfa: '추가 인증',
  contact_owner: '담당자 문의',
  complete_assignment: '배정 확인',
  switch_space: '스페이스 전환',
  restore_object: '복원',
  REFRESH_SESSION: '세션 새로고침',
};

export function resolveSuggestedActionLabel(suggestedAction: string): string | undefined {
  return (
    getSuggestedAction(suggestedAction)?.label ?? SUGGESTED_ACTION_FALLBACK_LABELS[suggestedAction]
  );
}

export interface CapabilityDeniedHintProps {
  reason?: string;
  suggestedAction?: string;
  onSuggestedAction?: (key: string) => void;
  testId?: string;
}

export function CapabilityDeniedHint({
  reason,
  suggestedAction,
  onSuggestedAction,
  testId = 'capability-denied-hint',
}: CapabilityDeniedHintProps) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const ctaLabel = suggestedAction ? resolveSuggestedActionLabel(suggestedAction) : undefined;
  const message = reason?.trim() || '이 작업을 수행할 권한이 없습니다.';

  const handleSuggestedAction = React.useCallback(
    (key: string) => {
      onSuggestedAction?.(key);
      if (!onSuggestedAction && key === 'step_up_mfa') {
        setModalOpen(true);
      }
    },
    [onSuggestedAction]
  );

  return (
    <>
      <div role="status" aria-live="polite" className={css(styles.root)} data-testid={testId}>
        <p className={css(styles.reason)}>
          <InfoCircleOutlined aria-hidden style={{ marginRight: 6 }} />
          {message}
        </p>
        {ctaLabel && suggestedAction ? (
          <Button
            type="link"
            size="small"
            className={css(styles.cta)}
            data-testid={`${testId}-cta`}
            onClick={() => handleSuggestedAction(suggestedAction)}
          >
            {ctaLabel}
          </Button>
        ) : null}
      </div>
      <StepUpMfaModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

export default CapabilityDeniedHint;
